import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from '@heroicons/react/24/outline';
import { useAuth, ROLE_SI_ADMIN, ROLE_SI_RH } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useClientsQuery } from '@/features/Clients/Data';
import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';
import {
  useSolicitacaoQuery,
  useMovimentacoesQuery,
  useSolicitacaoMutations,
  useRegrasClienteQuery,
  SI_SOLICITACOES_QUERY_KEYS,
  useEhDoTimeRhDoSolicitante,
} from '../../hooks/data';
import { GRUPO_TIPO_SOLICITACAO_COMPATIVEL, type TipoSolicitacao } from '../../models';
import { concluirSolicitacao } from '../../services';
import {
  TIPO_SOLICITACAO_LABELS,
  TIPOS_SOLICITACAO_SEM_MOVIMENTACAO,
} from '../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_SOLICITACAO_LABELS, STATUS_SOLICITACAO_BADGE_CLASS } from '../SolicitacoesListPage/solicitacoesListPage.definitions';
import { MOTIVO_EXCLUSAO_LABELS } from './solicitacaoDetailsPage.definitions';
import { pendenciaMovimentacao, pendenciaSolicitacao } from './pendenciaMovimentacao';
import MovimentacaoCard from './components/MovimentacaoCard';
import MovimentacoesResumoLista from './components/MovimentacoesResumoLista';
import SolicitacaoAcoesMenu, { type AcaoSolicitacao } from './components/SolicitacaoAcoesMenu';
import MovimentacoesExcluidasSection from './components/MovimentacoesExcluidasSection';
import NovaMovimentacaoModal from './components/NovaMovimentacaoModal';
import AcessosChecklist from './components/AcessosChecklist';
import EmailsClienteSection from './components/EmailCliente/EmailsClienteSection';
import PreCadastroAdmissaoSection from './components/PreCadastroAdmissaoSection';
import ColaboradorCabecalhoInfo from './components/ColaboradorCabecalhoInfo';
import VincularColaboradorModal from './components/VincularColaboradorModal';
import CancelarSolicitacaoModal from './components/CancelarSolicitacaoModal';
import ExcluirSolicitacaoModal, { type ExcluirSolicitacaoConfirmacao } from './components/ExcluirSolicitacaoModal';
import EditarTipoSolicitacaoModal from './components/EditarTipoSolicitacaoModal';
import EditarClienteModal from './components/EditarClienteModal';
import SolicitacaoRelacionadaInfo from './components/SolicitacaoRelacionadaInfo';
import MotivoSolicitacaoSection from './components/MotivoSolicitacaoSection';
import ObservacoesSolicitacaoSection from './components/ObservacoesSolicitacao/ObservacoesSolicitacaoSection';

interface SolicitacaoDetailsContentProps {
  id: string | undefined;
  /** Abre com esta Movimentação selecionada (ex.: clicada na visão Movimentações do Controle). */
  movimentacaoInicialId?: string | null;
  className?: string;
  /**
   * Força a visão de solicitante comum, mesmo pra quem tem role de SI — usado quando o
   * próprio SI abre, pelas abas "Que eu abri"/"Minha Equipe", uma solicitação que ele
   * mesmo criou (pra si ou pra sua equipe). A execução/controle do SI (Acessos
   * editável, Movimentação editável, Vincular Colaborador) só fica disponível entrando
   * pela aba "Controle de Solicitações" — decisão de produto explícita do time de SI,
   * pra centralizar onde o trabalho operacional acontece.
   */
  forcarVisaoSolicitante?: boolean;
  /** EXPERIMENTO — pisca o banner "Próximo passo pra concluir" ao abrir, usado quando o
   * Kanban redireciona pra cá em vez de deixar um drag pra "Concluída" só falhar (ver
   * SolicitacoesKanban.handleDrop). Só o efeito visual inicial — não muda nada no dado. */
  destacarPendencia?: boolean;
}

/**
 * Conteúdo de detalhe/execução de uma Solicitação — extraído de SolicitacaoDetailsPage
 * pra ser reaproveitado tanto pela rota (`/si/solicitacoes/:id`, deep-link) quanto pelo
 * SolicitacaoDrawer (aberto direto da lista, sem navegar). A mesma tela serve para
 * leitura (colaborador, gestor, quem abriu) e execução (SI), condicionando os controles
 * por role/ownership. A autorização real está em firestore.rules; aqui só decidimos o
 * que mostrar.
 */
export default function SolicitacaoDetailsContent({
  id,
  className,
  forcarVisaoSolicitante,
  destacarPendencia,
  movimentacaoInicialId,
}: SolicitacaoDetailsContentProps) {
  const { user, userHasRole } = useAuth();
  const toast = useToast();

  const isSI = userHasRole(ROLE_SI_ADMIN) && !forcarVisaoSolicitante;
  const isRH = userHasRole(ROLE_SI_RH) && !forcarVisaoSolicitante;

  const { data: solicitacao, isLoading, error } = useSolicitacaoQuery(id);
  const { data: movimentacoes = [] } = useMovimentacoesQuery(id);
  const {
    atualizarSolicitacao,
    cancelarSolicitacao,
    reativarSolicitacao,
    atualizarOperacional,
    excluirSolicitacao,
  } = useSolicitacaoMutations();
  const { data: clientes } = useClientsQuery();
  // Quem abriu a Solicitação — visível pra solicitante, gestor e SI, sem edição (só
  // informativo, ex.: SI precisa tirar dúvida com quem pediu). Busca ao vivo em vez de
  // um snapshot no documento — sempre reflete o nome atual, sem precisar migrar dado
  // antigo.
  const { data: solicitanteInfo } = useCollaboratorByIdQuery(solicitacao?.solicitanteId);
  const { data: regrasCliente = [] } = useRegrasClienteQuery();
  const queryClient = useQueryClient();

  const [modalMovimentacaoAberto, setModalMovimentacaoAberto] = useState(false);
  const [modalVincularAberto, setModalVincularAberto] = useState(false);
  const [modalCancelarAberto, setModalCancelarAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalTipoAberto, setModalTipoAberto] = useState(false);
  const [modalClienteAberto, setModalClienteAberto] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  // Movimentação aberta grande ao lado da lista (null = a com pendência, senão a 1ª).
  const [movimentacaoSelecionadaId, setMovimentacaoSelecionadaId] = useState<string | null>(movimentacaoInicialId ?? null);
  // EXPERIMENTO — pisca por ~1.8s e apaga sozinho; não precisa persistir nem reagir a mais
  // nada além da montagem (destacarPendencia só chega como `true` uma vez, no clique que
  // trouxe a pessoa pra cá).
  const [pendenciaEmDestaque, setPendenciaEmDestaque] = useState(destacarPendencia ?? false);

  React.useEffect(() => {
    if (!pendenciaEmDestaque) return;
    const timer = setTimeout(() => setPendenciaEmDestaque(false), 1800);
    return () => clearTimeout(timer);
  }, [pendenciaEmDestaque]);

  // Antes dos retornos antecipados abaixo (Regras de Hooks).
  const ehDoTimeRhDoSolicitante = useEhDoTimeRhDoSolicitante(solicitacao?.solicitanteEmail);

  if (isLoading) return <p className="p-6 text-sm text-gray-500">Carregando...</p>;
  if (error || !solicitacao) {
    return <p className="p-6 text-sm text-red-600">Solicitação não encontrada ou sem permissão de acesso.</p>;
  }

  const clienteNome = clientes.find((cliente) => cliente.id === solicitacao.clienteId)?.name ?? null;
  const naoTemMovimentacao = TIPOS_SOLICITACAO_SEM_MOVIMENTACAO.includes(solicitacao.tipoSolicitacao);
  const regraCliente = regrasCliente.find((regra) => regra.clienteId === solicitacao.clienteId);
  const propriedadeVisivel = regraCliente?.permiteEquipamentoCliente === true;
  const homologadoVisivel = regraCliente?.usaEquipamentoHomologado === true;

  const isSolicitante = solicitacao.solicitanteId === user?.collaboratorId;
  // Time RH: quem tem si_rh age como quem abriu nos chamados de outra pessoa do time (cobrir
  // férias/licença) — mesma regra de firestore.rules (siAgeComoSolicitante). A senha do
  // equipamento continua só pro próprio solicitante (podeVerSenha abaixo).
  const ageComoSolicitante = isSolicitante || ehDoTimeRhDoSolicitante;
  const isColaboradorAlvo = solicitacao.colaboradorId != null && solicitacao.colaboradorId === user?.collaboratorId;
  // si_rh só edita os chamados do próprio time (ver firestore.rules, "Edição de dados/contexto").
  const podeEditarContexto = isSI || ageComoSolicitante;
  const podeCancelarReativar = isSI || ageComoSolicitante;
  const podeExecutar = isSI;
  // Senha do equipamento: só SI e quem abriu a solicitação podem ver — inclusive quando
  // o próprio SI está vendo pela aba "Que eu abri" (forcarVisaoSolicitante), porque nesse
  // caso ele também é o solicitante. Usa a role real (sem o forçado de visão) porque a
  // visibilidade de senha não depende de "por qual aba entrou", só de quem é.
  const podeVerSenha = userHasRole(ROLE_SI_ADMIN) || isSolicitante;
  const pendenteDeVinculo = solicitacao.tipoSolicitacao === 'admissao' && solicitacao.colaboradorId == null;
  // Mostra independente do status (Novo, Em Andamento, ou qualquer outro) — decisão de
  // produto: "o que falta" é útil de ver mesmo antes de começar a tratar. Só pro SI, que é
  // quem executa essas pendências.
  const pendencia = podeExecutar ? pendenciaSolicitacao(solicitacao, movimentacoes) : null;
  // Movimentação de onde vem o "Próximo passo" (quando vem de uma) — o botão "Ir para o passo".
  const movimentacaoDaPendencia = pendencia
    ? movimentacoes.find((m) => pendenciaMovimentacao(m)?.label === pendencia.label) ?? null
    : null;
  const movimentacaoSelecionada =
    movimentacoes.find((m) => m.id === movimentacaoSelecionadaId) ??
    movimentacoes.find((m) => m.statusMovimentacao !== 'cancelada' && pendenciaMovimentacao(m)) ??
    movimentacoes.find((m) => m.statusMovimentacao !== 'cancelada') ??
    movimentacoes[0] ??
    null;
  const podeCriarMovimentacao =
    podeExecutar && solicitacao.status !== 'cancelada' && solicitacao.status !== 'concluida' && solicitacao.status !== 'excluida';

  const handleCancelar = (motivoCancelamento: string, solicitacaoRelacionadaId: string | null) => {
    if (!user) return;

    cancelarSolicitacao.mutate(
      {
        id: solicitacao.id,
        canceledBy: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
        motivoCancelamento,
      },
      {
        onSuccess: () => {
          setModalCancelarAberto(false);
          if (solicitacaoRelacionadaId) {
            atualizarOperacional.mutate({ id: solicitacao.id, solicitacaoRelacionadaId });
          }
        },
      }
    );
  };

  const handleExcluir = ({
    motivoExclusaoTipo,
    motivoExclusao,
    solicitacaoRelacionadaId,
  }: ExcluirSolicitacaoConfirmacao) => {
    if (!user) return;

    excluirSolicitacao.mutate(
      {
        id: solicitacao.id,
        excludedBy: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
        motivoExclusaoTipo,
        motivoExclusao,
        solicitacaoRelacionadaId,
      },
      { onSuccess: () => setModalExcluirAberto(false) }
    );
  };

  const handleReativar = () => {
    reativarSolicitacao.mutate({ id: solicitacao.id });
  };

  const handleConcluir = async () => {
    setConcluindo(true);
    try {
      await concluirSolicitacao(solicitacao.id);
      toast.success('Solicitação concluída.');
      // concluirSolicitacao é uma Cloud Function chamada direto (não passa pelas
      // mutations do TanStack Query), então precisa invalidar manualmente — sem isso a
      // tela continuava mostrando "em_andamento" (dado em cache) até um F5, deixando
      // clicar em "Concluir Solicitação" de novo e cair no erro de status.
      queryClient.invalidateQueries({ queryKey: SI_SOLICITACOES_QUERY_KEYS.all });
    } catch (error: any) {
      toast.error(`Não foi possível concluir: ${error?.message || 'requisitos pendentes'}`);
    } finally {
      setConcluindo(false);
    }
  };

  const handleCopiarLink = async () => {
    const link = `${window.location.origin}/si/solicitacoes/${solicitacao.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado!');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const irParaPasso = () => {
    if (!movimentacaoDaPendencia) return;
    setMovimentacaoSelecionadaId(movimentacaoDaPendencia.id);
    requestAnimationFrame(() =>
      document.getElementById('movimentacao-selecionada')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  };

  const acoesMenu: AcaoSolicitacao[] = [
    { label: 'Copiar link', onClick: handleCopiarLink },
    ...(podeCancelarReativar && (solicitacao.status === 'aberta' || solicitacao.status === 'em_andamento')
      ? [{ label: 'Cancelar solicitação', onClick: () => setModalCancelarAberto(true) }]
      : []),
    ...(podeCancelarReativar && solicitacao.status === 'cancelada'
      ? [{ label: 'Reativar solicitação', onClick: handleReativar }]
      : []),
    ...(isSI && solicitacao.status !== 'excluida'
      ? [{ label: 'Excluir solicitação', onClick: () => setModalExcluirAberto(true), perigosa: true }]
      : []),
  ];

  const handleConfirmarTrocaTipo = (novoTipo: TipoSolicitacao) => {
    atualizarSolicitacao.mutate({ id: solicitacao.id, tipoSolicitacao: novoTipo });
    setModalTipoAberto(false);
  };

  const handleConfirmarTrocaCliente = (novoClienteId: string) => {
    atualizarSolicitacao.mutate({ id: solicitacao.id, clienteId: novoClienteId });
    setModalClienteAberto(false);
  };

  return (
    <div className={className ?? 'space-y-6 p-6'}>
      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex flex-wrap items-center gap-1 font-montserrat text-2xl font-bold text-gray-900">
            <span className="inline-flex items-center gap-1">
              {TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao]}
              {podeEditarContexto && GRUPO_TIPO_SOLICITACAO_COMPATIVEL.includes(solicitacao.tipoSolicitacao) && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setModalTipoAberto(true)}
                  title="Alterar Tipo de Solicitação"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </span>
            {clienteNome && (
              <span className="inline-flex items-center gap-1 text-lg font-normal text-gray-500">
                - {clienteNome}
                {podeEditarContexto && (
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setModalClienteAberto(true)}
                    title="Alterar Cliente/Projeto"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            )}
          </h1>
          <ColaboradorCabecalhoInfo solicitacao={solicitacao} mostrarDadosSensiveis={isSI || isRH} />
          {/* A data prevista fica em cada Movimentação (DataPrevistaMovimentacao) — o envio e a
              devolução podem ter datas diferentes; no cabeçalho confundia. */}
          <p className="text-xs text-gray-400">
            Aberta por: {solicitanteInfo?.name ?? solicitacao.solicitanteEmail}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-sm font-semibold ${STATUS_SOLICITACAO_BADGE_CLASS[solicitacao.status]}`}
          >
            {STATUS_SOLICITACAO_LABELS[solicitacao.status]}
          </span>
          {podeExecutar && solicitacao.status === 'em_andamento' && (
            <button type="button" className="btn-primary" onClick={handleConcluir} disabled={concluindo}>
              {concluindo ? 'Concluindo...' : 'Concluir'}
            </button>
          )}
          <SolicitacaoAcoesMenu acoes={acoesMenu} />
        </div>
      </div>

      {pendencia && (
        <div
          className={`rounded-md border border-amber-300 bg-amber-50 p-3 transition-shadow duration-500 ${
            pendenciaEmDestaque ? 'animate-pulse ring-4 ring-amber-400' : ''
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-amber-800">
              Próximo passo pra concluir: {pendencia.emoji} {pendencia.label}
            </p>
            {movimentacaoDaPendencia && (
              <button type="button" className="btn-primary text-sm" onClick={irParaPasso}>
                Ir para o passo
              </button>
            )}
          </div>
        </div>
      )}

      <MotivoSolicitacaoSection solicitacao={solicitacao} podeEditar={podeEditarContexto} />
      </section>

      {solicitacao.status === 'cancelada' && solicitacao.motivoCancelamento && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-medium uppercase text-gray-400">Motivo do cancelamento</p>
          <p className="mt-1 text-sm text-gray-700">{solicitacao.motivoCancelamento}</p>
          {solicitacao.solicitacaoRelacionadaId && (
            <SolicitacaoRelacionadaInfo solicitacaoRelacionadaId={solicitacao.solicitacaoRelacionadaId} />
          )}
        </div>
      )}

      {solicitacao.status === 'excluida' && solicitacao.motivoExclusaoTipo && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-medium uppercase text-red-400">Motivo da exclusão</p>
          <p className="mt-1 text-sm text-red-700">
            {MOTIVO_EXCLUSAO_LABELS[solicitacao.motivoExclusaoTipo]}
            {solicitacao.motivoExclusaoTipo === 'outro' && solicitacao.motivoExclusao && `: ${solicitacao.motivoExclusao}`}
          </p>
          {solicitacao.solicitacaoRelacionadaId && (
            <SolicitacaoRelacionadaInfo solicitacaoRelacionadaId={solicitacao.solicitacaoRelacionadaId} />
          )}
        </div>
      )}

      {pendenteDeVinculo && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">Pendente de vínculo</p>
          <p className="mt-1 text-sm text-amber-700">
            Esta Admissão ainda não está vinculada a um colaborador real no Digital.
            É obrigatório vincular antes de concluir a solicitação.
          </p>
          {isSI && (
            <button
              type="button"
              className="btn-secondary mt-3 text-sm"
              onClick={() => setModalVincularAberto(true)}
            >
              Vincular Colaborador
            </button>
          )}
        </div>
      )}

      {(isSI || isRH) && (
        <PreCadastroAdmissaoSection
          preCadastroId={solicitacao.preCadastroId}
          colaboradorPendente={solicitacao.colaboradorPendente}
          vinculoManual={
            isSI && solicitacao.tipoSolicitacao === 'admissao' && solicitacao.status !== 'excluida'
              ? { solicitacaoId: solicitacao.id, nome: solicitacao.colaboradorNome, email: solicitacao.colaboradorEmail }
              : null
          }
        />
      )}

      {isSI && solicitacao.status !== 'excluida' && solicitacao.status !== 'cancelada' && (
        <EmailsClienteSection solicitacao={solicitacao} movimentacoes={movimentacoes} />
      )}

      <AcessosChecklist solicitacao={solicitacao} podeEditar={isSI} />

      {/* Formatação (e outros tipos futuros em TIPOS_SOLICITACAO_SEM_MOVIMENTACAO) nunca
          tem Movimentação nenhuma — a seção inteira não faz sentido pra esses tipos. */}
      {!naoTemMovimentacao && (
        <div className="space-y-3">
          {movimentacoes.length === 0 && (
            <>
              <p className="font-montserrat text-lg font-medium text-gray-900">Movimentações</p>
              <p className="text-sm text-gray-500">Nenhuma movimentação registrada.</p>
              {podeCriarMovimentacao && (
                <button type="button" className="btn-secondary text-sm" onClick={() => setModalMovimentacaoAberto(true)}>
                  Nova Movimentação
                </button>
              )}
            </>
          )}
          {movimentacaoSelecionada && (
          <div className="flex flex-wrap items-start gap-5">
            <div className="min-w-0 flex-[1_1_220px] lg:max-w-[260px]">
              <MovimentacoesResumoLista
                movimentacoes={movimentacoes}
                selecionadaId={movimentacaoSelecionada.id}
                onSelecionar={setMovimentacaoSelecionadaId}
                mostrarPendencia={podeExecutar}
                onNova={podeCriarMovimentacao ? () => setModalMovimentacaoAberto(true) : undefined}
              />
            </div>
            <div id="movimentacao-selecionada" className="min-w-0 flex-[999_1_480px] scroll-mt-4">
          {[movimentacaoSelecionada].map((movimentacao) => (
            <MovimentacaoCard
              key={movimentacao.id}
              solicitacaoId={solicitacao.id}
              movimentacao={movimentacao}
              podeEditar={podeExecutar}
              podeEditarDataPrevista={isSI || ageComoSolicitante}
              podeExcluir={isSI && solicitacao.status !== 'concluida' && solicitacao.status !== 'excluida'}
              podeVerSenha={podeVerSenha}
              isColaboradorAlvo={isColaboradorAlvo}
              propriedadeVisivel={propriedadeVisivel}
              homologadoVisivel={homologadoVisivel}
              colaboradorId={solicitacao.colaboradorId ?? null}
              tipoSolicitacao={solicitacao.tipoSolicitacao}
              preCadastroId={solicitacao.preCadastroId ?? null}
              nomeColaboradorPendente={solicitacao.colaboradorPendente?.nome ?? null}
              clientes={clientes}
              regrasCliente={regrasCliente}
              solicitacaoClienteId={solicitacao.clienteId ?? null}
            />
          ))}
            </div>
          </div>
          )}
          <MovimentacoesExcluidasSection solicitacaoId={solicitacao.id} />
        </div>
      )}

      {/* Alocação agora é por Movimentação (AlocacaoMovimentacaoLinha, no card) — cada
          responsável vê só a Movimentação dele. */}
      <ObservacoesSolicitacaoSection solicitacaoId={solicitacao.id} observacaoAnterior={solicitacao.observacoes} />

      <NovaMovimentacaoModal
        solicitacaoId={solicitacao.id}
        isOpen={modalMovimentacaoAberto}
        onClose={() => setModalMovimentacaoAberto(false)}
        clienteNome={clienteNome}
        clienteId={solicitacao.clienteId ?? null}
        propriedadeVisivel={propriedadeVisivel}
        homologadoVisivel={homologadoVisivel}
        colaboradorId={solicitacao.colaboradorId ?? null}
        alocacaoReferenciadaId={solicitacao.alocacaoReferenciadaId ?? null}
      />

      <VincularColaboradorModal
        solicitacaoId={solicitacao.id}
        colaboradorPendente={solicitacao.colaboradorPendente}
        isOpen={modalVincularAberto}
        onClose={() => setModalVincularAberto(false)}
      />

      <CancelarSolicitacaoModal
        isOpen={modalCancelarAberto}
        onClose={() => setModalCancelarAberto(false)}
        idAtual={solicitacao.id}
        onConfirmar={handleCancelar}
        confirmando={cancelarSolicitacao.isPending}
      />

      <ExcluirSolicitacaoModal
        isOpen={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        idAtual={solicitacao.id}
        onConfirmar={handleExcluir}
        confirmando={excluirSolicitacao.isPending}
      />

      <EditarTipoSolicitacaoModal
        isOpen={modalTipoAberto}
        onClose={() => setModalTipoAberto(false)}
        tipoAtual={solicitacao.tipoSolicitacao}
        onConfirmar={handleConfirmarTrocaTipo}
        confirmando={atualizarSolicitacao.isPending}
      />

      <EditarClienteModal
        isOpen={modalClienteAberto}
        onClose={() => setModalClienteAberto(false)}
        clienteIdAtual={solicitacao.clienteId ?? null}
        onConfirmar={handleConfirmarTrocaCliente}
        confirmando={atualizarSolicitacao.isPending}
      />
    </div>
  );
}
